import type { PageDto } from "@eu.mdotm.api/sphere";
import { runWithErrorReporting } from "$root/api/error-reporting/report";
import { useFunctionalAreas } from "$root/App/context";
import { useCrumbs } from "$root/components/Crumbs/useCrumbs";
import { paperPlaneId } from "$root/components/EvolvedPrint/configuration/shared";
import { LeavePrompt } from "$root/components/LeavePrompt";
import { PageHeader } from "$root/components/PageHeader";
import { typedUrlForRoute, useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import ReactQueryWrapper from "$root/components/ReactQueryWrapper";
import { Sidebar } from "$root/components/common/Sidebar";
import { SideDrawer } from "$root/components/common/SideDrawer";
import { spawnCreateDialog } from "$root/components/spawnable/entity-management/create-dialog";
import { downloadSinglePdf, generateReportBuilderTemplateName } from "$root/functional-areas/pdf";
import { PortfolioStudioSettingTabEnum } from "$root/functional-areas/portfolio-studio-settings/tab-enum";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import {
	DefaultReportTemplateName,
	defaultPortfolioTemplates,
	extractWidgetConfig,
	isDefaultReportTemplate,
	type DefaultReportTemplate,
} from "$root/functional-areas/reports/default-templates";
import { reportRepository } from "$root/functional-areas/reports/repository";
import { platformToast } from "$root/notification-system/toast";
import Report from "$root/pages/Report";
import { AsOfButton } from "$root/pages/PortfolioDetails/as-of/AsOfButton";
import { isAsOfReadable, useAsOfSnapshot } from "$root/pages/PortfolioDetails/as-of/useAsOfSnapshot";
import { useQuery } from "@tanstack/react-query";
import { type RequireProperty, qualifier } from "$root/utils";
import type { Abortable } from "@mdotm/utils/promise";
import { Card } from "$root/widgets-architecture/layout/Card";
import {
	type DraggableListItemProps,
	type IconName,
	type InnerRefProps,
	type StylableProps,
	AutoTooltip,
	ClickableDiv,
	DraggableList,
	ErrorBoundary,
	Icon,
	Tab,
	TabGroup,
	Text,
} from "@mdotm/shadcn/legacy/components";
import { Select, Button, IconButton } from "@mdotm/shadcn/mdotm/components";
import {
	WrapIfStrOrNumOrEmpty,
	adaptAnimatedNodeProvider,
	overrideClassList,
	spawn,
	toClassName,
	useUnmountAbortSignal,
} from "@mdotm/shadcn/legacy/react-extensions";
import { ForEach } from "@mdotm/utils/react-extra";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { noop, replaceById, sleep } from "@mdotm/shadcn/legacy/utils";
import React, {
	type ForwardedRef,
	type MouseEvent,
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import {
	type ReportTemplate,
	type ReportTemplateItem,
	type ReportTemplateStatus,
	type ReportTemplateUnion,
	type ReportTemplateVariant,
	availableTemplateItemKindsByReportType,
	enabledTemplateItemKinds,
	itemFactories,
	itemToAdderLabelMap,
	itemToConstraints,
	itemToIconMap,
	itemToLabelMap,
	latestVersion,
	migrateToLatest,
} from "../../../functional-areas/reports/report-latest";
import {
	BondsDividendsTracker,
	Commentary,
	Composition,
	Cover,
	Disclaimer,
	ExAnteContributionVolatility,
	ExAntePortfolioMetrics,
	Exposure,
	FactorExposure,
	FreeText,
	MaturityCouponTracker,
	Performance,
	PerformanceAndVolatilityContribution,
	PerformanceAttribution,
	PortfolioMetrics,
	Summary,
} from "./report-components";
import { trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import { IconWalls } from "$root/components/IconWall";

function ScrollWrapper({ children }: { children: React.ReactNode }) {
	const portraitContainerRef = useRef<HTMLDivElement>(null);
	const intervalIdRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

	useEffect(() => {
		portraitContainerRef.current?.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
	}, []);

	useEffect(() => {
		const scrollWrapper = portraitContainerRef.current;
		if (scrollWrapper !== null) {
			intervalIdRef.current = setInterval(() => {
				const isPaperplaneTagVisible = document.getElementById(paperPlaneId) != null;
				if (isPaperplaneTagVisible) {
					clearInterval(intervalIdRef.current);
				}

				scrollWrapper.scrollTo({ top: scrollWrapper.scrollHeight, behavior: "smooth" });
			}, 700);

			scrollWrapper.addEventListener("wheel", () => {
				clearInterval(intervalIdRef.current);
			});
		}
	}, []);

	return (
		<div className="overflow-y-auto overflow-scroll h-full" ref={portraitContainerRef}>
			{children}
		</div>
	);
}
type IncompatibleVersion = { kind: "incompatible-version"; templateName: string };
type ReportEditorLoadResult = ReportTemplate | Omit<DefaultReportTemplate, "visible"> | IncompatibleVersion;

function ReportEditor(props: { reportUid: string }): React.JSX.Element {
	return (
		<ReactQueryWrapper
			queryKey={["reportTemplate", props.reportUid]}
			queryFn={async (): Promise<ReportEditorLoadResult> => {
				const defaultTemplate = defaultPortfolioTemplates.find((d) => d.id === props.reportUid);
				if (defaultTemplate) {
					return defaultTemplate;
				}

				const template = (await reportRepository.template.fetchQuery(props.reportUid)) as ReportTemplateUnion;

				if (template.data.version > latestVersion) {
					return { kind: "incompatible-version", templateName: template.templateName };
				}

				if (
					template.data.version < latestVersion ||
					template.templateName === defaultPortfolioTemplates[0].templateName
				) {
					const migratedTemplate = migrateToLatest(template);
					return migratedTemplate;
				}

				return template as ReportTemplate;
			}}
		>
			{(data) =>
				typeof data === "object" && "kind" in data && data.kind === "incompatible-version" ? (
					<IncompatibleVersionFallback reportUid={props.reportUid} templateName={data.templateName} />
				) : (
					<ReportEditorInner reportUid={props.reportUid} template={data as ReportTemplate | DefaultReportTemplate} />
				)
			}
		</ReactQueryWrapper>
	);
}

function IncompatibleVersionFallback(props: { reportUid: string; templateName: string }): React.JSX.Element {
	const crumbs = useCrumbs();
	return (
		<>
			<PageHeader
				crumbs={[
					...crumbs.storyfolio("Report builder", props.reportUid),
					...crumbs.reportName({ uuid: props.reportUid, name: props.templateName }),
				]}
			/>
			<div className="grow min-h-0 flex">
				<IconWalls.OutdatedClient />
			</div>
		</>
	);
}

const tabs = ["current", "proposal"] satisfies Array<ReportTemplateVariant>;
function ReportEditorInner(props: {
	reportUid: string;
	template: ReportTemplate | DefaultReportTemplate;
}): React.JSX.Element {
	const [templateVariantTab, setTemplateVariantTab] = useState<ReportTemplateVariant>("current");
	const [editableTemplate, _setEditableTemplate] = useState(props.template);
	const [isDirty, setIsDirty] = useState(false);
	const setEditableTemplate = useCallback((x: Parameters<typeof _setEditableTemplate>[0]) => {
		_setEditableTemplate(x);
		setIsDirty(true);
	}, []);

	// Expose the open report template to SphereAI so it can ground answers / navigate here.
	useFunctionalAreas(
		() => ({ areas: "report", data: { uuid: props.reportUid, name: props.template.templateName } }),
		[props.reportUid, props.template.templateName],
	);

	const [expandAdders, setExpandAdders] = useState(true);
	const [selectedPortfolio, setSelectedPortfolio] = useState<{ name: string; uuid: string } | null>(null);
	const [reloadCounter, setReloadCounter] = useState(0);
	// The editor's own as-of date. Deliberately local: Portfolio Details keeps its date in the URL,
	// and nothing carries over between the two surfaces.
	const [asOf, setAsOf] = useState<string | null>(null);
	const asOfSnapshot = useAsOfSnapshot(selectedPortfolio?.uuid, asOf ?? undefined);
	const pinnedPreviewReady = isAsOfReadable(asOf ?? undefined, asOfSnapshot);
	// `isAsOfReadable` is false for both `generating` and `error`, and `error` is terminal — nothing
	// retries on the page's behalf. Rendering one wall for both would leave a failed date on a
	// spinner for ever, so the two are told apart here as the `AsOfBar` tells them apart.
	const pinnedPreviewFailed = asOfSnapshot.kind === "error";
	// The PDF pipeline waits for a snapshot still being generated, so a download may be asked for
	// meanwhile; once that generation has failed there is nothing left to wait for and the request
	// would only spend the backend's 90-second ceiling before erroring.
	const reportReady = !pinnedPreviewFailed;
	// A date belongs to one portfolio on the Current tab, so either change drops it: the inception
	// bound moves with the portfolio, and a proposal has no stored snapshot to pin to (R11). Keyed on
	// the uuid rather than set from the `Select`'s `onChange`, because the portfolio is also chosen by
	// the query's `onSuccess`, whose key includes the variant tab. Adjusted during render rather than
	// in an effect so the preview never paints a frame with the stale date.
	const selectedPortfolioUid = selectedPortfolio?.uuid;
	const asOfOwner = `${selectedPortfolioUid ?? ""}|${templateVariantTab}`;
	const [lastAsOfOwner, setLastAsOfOwner] = useState(asOfOwner);
	if (asOfOwner !== lastAsOfOwner) {
		setLastAsOfOwner(asOfOwner);
		setAsOf(null);
	}
	// Only for the picker's lower bound; the selectable-portfolio list carries no inception date.
	// The same key the preview's summary read builds, so this is one request, not two.
	const selectedSummary = useQuery({
		...portfolioRepository.summary.getQueryConfig(selectedPortfolioUid ?? "", { enhanced: false }),
		enabled: selectedPortfolioUid != null && templateVariantTab !== "proposal",
	});
	// Disabled until the inception bound has landed: without it the calendar has no lower limit and
	// a pre-inception date pins happily.
	const asOfDisabled = selectedPortfolioUid == null || templateVariantTab === "proposal" || !selectedSummary.isSuccess;
	const { push } = useTypedNavigation();
	const scalingFactor = expandAdders ? minScalingFactor : maxScalingFactor;
	const history = useHistory();

	const [pathToNotBlock, setPathToNotBlock] = useState<string[]>([history.location.pathname]);

	const reportDtoConfig = useMemo<RequireProperty<PageDto, "name"> | undefined>(
		() =>
			selectedPortfolio
				? {
						templateId: isDefaultReportTemplate(editableTemplate) ? editableTemplate.id : editableTemplate.uuid,
						variant: templateVariantTab,
						objectId: selectedPortfolio.uuid,
						status: (reloadCounter === 0 ? "ready" : "draft") as ReportTemplateStatus,
						autoScroll: true,
						name: generateReportBuilderTemplateName(
							{ ...selectedPortfolio, choice: templateVariantTab === "current" ? "current" : "enhance" },
							editableTemplate.templateName,
							asOf ?? undefined,
						),
						...(asOf ? { asOfDate: asOf } : {}),
					}
				: undefined,
		[asOf, editableTemplate, reloadCounter, selectedPortfolio, templateVariantTab],
	);

	function checkIfNameIsAvailable(name: string, opts?: Abortable) {
		if (name.toLocaleLowerCase() === defaultPortfolioTemplates[0].templateName.toLocaleLowerCase()) {
			throw new Error("name already exist");
		}
		return reportRepository.isReportNameAvailable(name, opts);
	}

	async function updateTemplate(name?: string) {
		try {
			await reportRepository.update((props.template as any).uuid, {
				...editableTemplate,
				templateName: name ?? editableTemplate.templateName,
				data: {
					...editableTemplate.data,
					status: "ready",
				},
			});
			flushSync(() =>
				setPathToNotBlock((latestPath) => [
					...latestPath,
					typedUrlForRoute("PortfolioStudioSettings", { tab: PortfolioStudioSettingTabEnum.ReportCustomisation }),
				]),
			);
			platformToast({
				children: "Successfully update your template",
				icon: "pdf",
				severity: "success",
			});

			push("PortfolioStudioSettings", { tab: PortfolioStudioSettingTabEnum.ReportCustomisation });
		} catch (error) {
			platformToast({
				children: "Oops, Something went wrong, pleas re-try",
				icon: "pdf",
				severity: "error",
			});
			throw error;
		}
	}

	async function createTemplate(name?: string) {
		try {
			await reportRepository.create({
				...editableTemplate,
				templateName: name ?? editableTemplate.templateName,
				data: {
					...editableTemplate.data,
					status: "ready",
				},
			});
			flushSync(() =>
				setPathToNotBlock((latestPath) => [
					...latestPath,
					typedUrlForRoute("PortfolioStudioSettings", { tab: PortfolioStudioSettingTabEnum.ReportCustomisation }),
				]),
			);
			platformToast({
				children: `Successfully create template ${name}`,
				icon: "pdf",
				severity: "success",
			});
			push("PortfolioStudioSettings", { tab: PortfolioStudioSettingTabEnum.ReportCustomisation });
		} catch (error) {
			platformToast({
				children: "Oops, Something went wrong, pleas re-try",
				icon: "pdf",
				severity: "error",
			});
			throw error;
		}
	}

	async function updateTemplatePreview(config: typeof editableTemplate) {
		try {
			const sphereTemplate = defaultPortfolioTemplates[0];
			let uuid: string | undefined = (config as any)?.uuid;
			if (
				config.templateName === sphereTemplate.templateName &&
				(config as any)?.id === DefaultReportTemplateName.sphere
			) {
				// TODO(Ben): was there a BE-specific reason for this flow?
				// Can't we create the new template with all the data without having to update it?
				const savedDefaultTemplate = await reportRepository.create({
					templateName: config.templateName,
					visible: true,
					format: "pdf",
					data: {
						...config.data,
						status: "draft",
					},
				});
				uuid = savedDefaultTemplate.uuid;
				setEditableTemplate((prev) =>
					savedDefaultTemplate.uuid
						? ({
								uuid: savedDefaultTemplate.uuid,
								data: prev.data,
								format: prev.format,
								templateName: prev.templateName,
								visible: prev.visible,
							} satisfies ReportTemplate)
						: prev,
				);
				await sleep(500);
			}

			await reportRepository.update(uuid!, config);
		} catch (error) {
			platformToast({
				children: "Unable to update the portfolio preview",
				icon: "pdf",
				severity: "error",
			});
			throw error;
		}
	}

	const crumbs = useCrumbs();

	const { t } = useTranslation();

	return (
		<>
			<LeavePrompt
				title={t("LEAVE_PAGE")}
				when={isDirty}
				showCancelIcon={false}
				pathToNotBlock={["/login", ...pathToNotBlock]}
			>
				{t("LEAVE_PAGE_MESSAGE")}
			</LeavePrompt>

			<PageHeader
				crumbs={[
					...crumbs.storyfolio("Report builder", props.reportUid),
					...crumbs.reportName({
						uuid: props.reportUid,
						name: props.template.templateName,
					}),
				]}
				titleAction={
					<div className="flex items-center space-x-2">
						{isDefaultReportTemplate(props.template) ? (
							<>
								<Button
									palette="tertiary"
									size="small"
									onClick={() =>
										push("PortfolioStudioSettings", { tab: PortfolioStudioSettingTabEnum.ReportCustomisation })
									}
									data-qualifier={qualifier.reportCustomisation.builder.cancel}
								>
									Cancel
								</Button>
								<Button
									onClickAsync={() =>
										spawnCreateDialog({
											entityType: "template",
											onSubmitAsync: (name) => createTemplate(name),
											checkIfNameIsAvailable,
											placeholder: editableTemplate.templateName,
											header: "Save as",
										}).catch(noop)
									}
									palette="primary"
									size="small"
									data-qualifier={qualifier.reportCustomisation.builder.saveAs}
								>
									Save as
								</Button>
							</>
						) : (
							<>
								<Button
									palette="tertiary"
									size="small"
									onClick={() =>
										push("PortfolioStudioSettings", { tab: PortfolioStudioSettingTabEnum.ReportCustomisation })
									}
									data-qualifier={qualifier.reportCustomisation.builder.cancel}
								>
									Cancel
								</Button>

								<Button
									palette="primary"
									size="small"
									onClickAsync={() =>
										props.template.data.status === "draft"
											? spawnCreateDialog({
													entityType: "template",
													onSubmitAsync: (name) => updateTemplate(name),
													checkIfNameIsAvailable,
													placeholder: editableTemplate.templateName,
													header: "Rename Template",
												}).catch(noop)
											: updateTemplate()
									}
									data-qualifier={qualifier.reportCustomisation.builder.save}
								>
									{props.template.data.status === "draft" ? "Update" : "Done"}
								</Button>
							</>
						)}
					</div>
				}
			/>
			<div className="grow min-h-0 flex relative z-0">
				<Adders
					expand={expandAdders}
					onExpandChange={setExpandAdders}
					ctx={{ reportTemplate: editableTemplate, templateVariantTab }}
					activeTab={templateVariantTab}
					onAdd={(item) =>
						setEditableTemplate(
							(v) =>
								({
									...v,
									data: {
										...v.data,
										templateItemsByVariant: {
											...v.data.templateItemsByVariant,
											[templateVariantTab]:
												// the cover page is only meaningful as the first page, so it goes to the top rather than the end
												item.kind === "cover"
													? [item, ...v.data.templateItemsByVariant[templateVariantTab]]
													: [...v.data.templateItemsByVariant[templateVariantTab], item],
										},
									},
								}) as typeof v,
						)
					}
				/>
				<Card
					className={`grow rounded-none! pl-7 w-[480px] max-w-[480px] min-w-[480px] border-r border-r-[color:var(--palette-N50)] border-solid`}
					title="Template structure"
				>
					{/* spacer */}
					<div className="h-3" />
					<TabGroup
						showTitleDivider
						size="x-small"
						onTabChange={(index) => setTemplateVariantTab(tabs[index])}
						tabIndex={tabs.indexOf(templateVariantTab)}
					>
						<Tab title="Current">
							<TemplateItemList
								mode="edit"
								templateVariant="current"
								list={editableTemplate.data.templateItemsByVariant.current}
								onChange={(list) =>
									setEditableTemplate({
										...editableTemplate,
										data: {
											...editableTemplate.data,
											templateItemsByVariant: {
												...editableTemplate.data.templateItemsByVariant,
												current: list,
											},
											templateDraftItemsByVariant: {
												...editableTemplate.data.templateItemsByVariant,
												current: list,
											},
										},
									} as typeof editableTemplate)
								}
							/>
						</Tab>
						<Tab title="Proposal">
							<TemplateItemList
								mode="edit"
								templateVariant="proposal"
								list={editableTemplate.data.templateItemsByVariant.proposal}
								onChange={(list) =>
									setEditableTemplate({
										...editableTemplate,
										data: {
											...editableTemplate.data,
											templateItemsByVariant: {
												...editableTemplate.data.templateItemsByVariant,
												proposal: list,
											},
											templateDraftItemsByVariant: {
												...editableTemplate.data.templateDraftItemsByVariant,
												proposal: list,
											},
										},
									} as typeof editableTemplate)
								}
							/>
						</Tab>
					</TabGroup>
				</Card>
				<Card
					className="flex-1 rounded-none! overflow-hidden"
					title={
						<ReactQueryWrapper
							queryKey={["selectablePortfolioTemplate", templateVariantTab]}
							queryFn={async () => {
								const selectablePortfolios = await portfolioRepository.editorAvailablePortfolios.fetchQuery(
									null,
									"INVESTMENT",
								);

								return {
									options: (selectablePortfolios ?? []).flatMap((x) =>
										x.name &&
										x.uuid &&
										(x.status === "ACCEPTED" || x.status === "PROPOSAL_READY" || x.status === "READY")
											? [
													{
														label: x.name,
														value: { name: x.name, uuid: x.uuid },
														disabled: templateVariantTab === "proposal" && x.status !== "PROPOSAL_READY",
													},
												]
											: [],
									),
									selectablePortfolios: selectablePortfolios ?? [],
								};
							}}
							onSuccess={({ selectablePortfolios, options }) => {
								const portfolioWithProposal = selectablePortfolios.find(
									(x) => x.status === "PROPOSAL_READY" && x.uuid && x.name,
								);
								if (portfolioWithProposal) {
									return setSelectedPortfolio({ uuid: portfolioWithProposal.uuid!, name: portfolioWithProposal.name! });
								}
								const [portfolio] = options;

								if (portfolio) {
									return setSelectedPortfolio({ uuid: portfolio.value.uuid, name: portfolio.value.name });
								}
							}}
							keepPreviousData
							loadingFallback={
								<div className="flex flex-wrap items-center grow gap-2 min-w-0 animate-pulse">
									<Select
										options={[]}
										value={selectedPortfolio}
										onChange={setSelectedPortfolio}
										enableSearch
										className="basis-0 grow shrink min-w-[7rem] max-w-48"
										disabled
									/>
									<AsOfButton
										setAsOf={noop}
										snapshot={{ kind: "live" }}
										qualifier={qualifier.reportCustomisation.builder.asOfDate}
										disabled
									/>
									<Button palette="tertiary" size="small" onClickAsync={noop} disabled className="shrink-0">
										Reload
									</Button>
									<IconButton
										className="shrink-0 ml-auto"
										size={16}
										icon="Dowload"
										color={themeCSSVars.palette_N400}
										disabled
										onClickAsync={noop}
									/>
								</div>
							}
						>
							{({ options }) => (
								<div className="flex flex-wrap items-center grow gap-2 min-w-0">
									{/* The row takes as many lines as it needs and no more. One flat wrap container, because a
									    group that wraps inside a row that wraps spends a line on each. The select is the elastic
									    one, and `basis-0` is what makes it shrink before the row breaks — flexbox chooses line
									    breaks from flex base sizes, so a wider basis wraps while there is still width to give.
									    `ml-auto` keeps the download at the right of whichever line it lands on. Both copies of
									    the row carry the same classes; the skeleton would otherwise jump on resolve. */}
									<Select
										options={options}
										value={selectedPortfolio}
										onChange={setSelectedPortfolio}
										enableSearch
										className="basis-0 grow shrink min-w-[7rem] max-w-48"
										data-qualifier={qualifier.reportCustomisation.builder.selectPortfolio}
									/>
									<AsOfButton
										asOf={asOf ?? undefined}
										setAsOf={setAsOf}
										snapshot={asOfSnapshot}
										inceptionDate={selectedSummary.data?.creationDate}
										qualifier={qualifier.reportCustomisation.builder.asOfDate}
										disabled={asOfDisabled}
									/>
									<Button
										palette="tertiary"
										className="shrink-0"
										size="small"
										disabled={isDefaultReportTemplate(editableTemplate)}
										onClickAsync={async () => {
											await updateTemplatePreview({
												...editableTemplate,
												data: {
													status: editableTemplate.data.status,
													version: editableTemplate.data.version,
													templateDraftItemsByVariant: editableTemplate.data.templateDraftItemsByVariant,
													templateItemsByVariant: props.template.data.templateItemsByVariant,
												},
											});
											setReloadCounter((x) => x + 1);
										}}
										data-qualifier={qualifier.reportCustomisation.builder.reloadPreview}
									>
										Reload
									</Button>
									<IconButton
										className="shrink-0 ml-auto"
										size={16}
										icon="Dowload"
										color={themeCSSVars.palette_N400}
										disabled={reportDtoConfig === undefined || !reportReady}
										onClickAsync={async () => {
											if (reportDtoConfig && reportReady) {
												await runWithErrorReporting(
													async () => {
														await downloadSinglePdf(reportDtoConfig.name, reportDtoConfig);
														if (selectedPortfolio) {
															trackMixPanelEvent("portfolio.entityAction.downloadReport", {
																portfolio: { uuid: selectedPortfolio.uuid, name: selectedPortfolio.name },
																template: { uuid: props.reportUid, name: props.template.templateName },
															});
														}
													},
													{
														area: "portfolio",
														attemptedOperation: {
															message: "download report template preview",
															payload: JSON.stringify(reportDtoConfig),
														},
													},
												).catch(noop);
											}
										}}
										data-qualifier={qualifier.reportCustomisation.builder.downloadTemplate}
									/>
								</div>
							)}
						</ReactQueryWrapper>
					}
				>
					<div
						style={{
							paddingTop: "1rem",
							paddingBottom: "1rem",
							backgroundColor: "white",
							height: `calc(100dvh - 140px)`,
							position: "relative",
						}}
						data-qualifier={qualifier.reportCustomisation.builder.previewContainer}
					>
						{showIframe && selectedPortfolio && (
							<div className="overflow-hidden h-full" key={reloadCounter}>
								<div
									style={{
										height: `${100 / scalingFactor}%`,
										width: `${100 / scalingFactor}%`,
										transform: `scale(${scalingFactor})`,
										transformOrigin: "top left",
									}}
								>
									<ScrollWrapper key={selectedPortfolio.uuid}>
										<ErrorBoundary errorChildren={<IconWalls.ErrorData />}>
											{/* Mounted only once the pinned date can be read: rendering it meanwhile would show
											    today's figures under the date just picked. */}
											{pinnedPreviewFailed ? (
												<IconWalls.ErrorData />
											) : pinnedPreviewReady ? (
												<Report
													templateId={
														isDefaultReportTemplate(editableTemplate) ? editableTemplate.id : editableTemplate.uuid
													}
													autoScroll="true"
													objectId={selectedPortfolio.uuid}
													variant={templateVariantTab}
													status={(reloadCounter === 0 ? "ready" : "draft") as ReportTemplateStatus}
													asOfDate={asOf ?? undefined}
												/>
											) : (
												<IconWalls.CalculatingData />
											)}
										</ErrorBoundary>
									</ScrollWrapper>
								</div>
							</div>
						)}
					</div>
				</Card>
			</div>
		</>
	);
}

const showIframe = true;
const minScalingFactor = 0.4;
const maxScalingFactor = 0.8;

export default ReportEditor;

function TemplateItem(
	props: Partial<DraggableListItemProps<ReportTemplateItem>> &
		InnerRefProps<HTMLDivElement> & { item: ReportTemplateItem; onEdit?(): void; onDelete?(): void },
): React.JSX.Element {
	return (
		<div
			style={{ borderColor: themeCSSVars.palette_N50 }}
			className={toClassName({
				"relative flex flex-row items-center min-w-0 bg-white transition-shadow border-b gap-2 px-2": true,
				"shadow-lg": props.isDragging,
				"border-t": props.isDragging,
			})}
			{...props.draggableProps}
			ref={props.innerRef}
		>
			<div
				{...props.dragHandleProps}
				className={toClassName({ invisible: !props.draggableProps || !props.dragHandleProps })}
			>
				<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path
						fillRule="evenodd"
						clipRule="evenodd"
						d="M6.04181 3.2944L7.96096 1.37506C8.01677 1.31944 8.10704 1.31944 8.16286 1.37506L10.082 3.2944C10.1228 3.3352 10.1349 3.3966 10.113 3.44991C10.0909 3.50323 10.0389 3.53807 9.98115 3.53807H6.14267C6.08493 3.53807 6.03277 3.50323 6.01083 3.44991C5.98888 3.3966 6.00101 3.3352 6.04181 3.2944ZM8.16305 14.8297C8.10724 14.8853 8.01697 14.8853 7.96115 14.8297L6.04181 12.9103C6.00101 12.8695 5.98888 12.8081 6.01083 12.7548C6.03296 12.7015 6.08512 12.6667 6.14286 12.6667H9.98134C10.0391 12.6667 10.091 12.7015 10.1132 12.7548C10.1351 12.8081 10.123 12.8695 10.0822 12.9103L8.16305 14.8297ZM14.3446 6.00001H1.97822C1.62186 6.00001 1.33337 6.29415 1.33337 6.65052C1.33337 7.00688 1.33337 7.30102 1.97822 7.30102H14.3333C14.701 7.30102 14.9895 7.01254 14.9895 6.65052C14.9895 6.28849 14.701 6.00001 14.3446 6.00001ZM1.97822 9.33334H14.3446C14.701 9.33334 14.9895 9.62183 14.9895 9.98385C14.9895 10.3459 14.701 10.6344 14.3333 10.6344H1.97822C1.33337 10.6344 1.33337 10.3402 1.33337 9.98385C1.33337 9.62748 1.62186 9.33334 1.97822 9.33334Z"
						fill="#8792AB"
					/>
				</svg>
			</div>
			<Icon icon={itemToIconMap[props.item.kind]} size={24} color={themeCSSVars.palette_P500} />
			<Text type="Body/M/Book">{itemToLabelMap[props.item.kind]}</Text>
			<div className="flex items-center ml-auto gap-2">
				<IconButton
					style={{
						visibility: props.onEdit ? undefined : "hidden",
					}}
					onClick={() => props.onEdit?.()}
					icon="Edit"
					size={16}
					color={themeCSSVars.palette_N300}
					data-qualifier={qualifier.reportCustomisation.builder.widgetItem.edit(props.item.kind)}
				/>
				<IconButton
					style={{
						visibility: props.onDelete ? undefined : "hidden",
					}}
					onClick={() => props.onDelete?.()}
					icon="Delete"
					size={16}
					color={themeCSSVars.palette_N300}
					data-qualifier={qualifier.reportCustomisation.builder.widgetItem.delete(props.item.kind)}
				/>
			</div>
		</div>
	);
}

type AddButtonPalette = "neutral";
const paletteToIconColor: Record<AddButtonPalette, string> = {
	neutral: themeCSSVars.palette_N400,
};

function AddButton(
	props: {
		onClick(e: MouseEvent): void;
		disabled?: boolean;
		icon: IconName;
		palette?: AddButtonPalette;
		size?: "small" | "large";
		children: ReactNode;
		subtitle?: string;
		innerRef?: ForwardedRef<HTMLDivElement>;
		"data-qualifier"?: string;
	} & StylableProps,
): React.JSX.Element {
	const palette = props.palette ?? "neutral";
	const size = props.size ?? "small";
	return (
		<ClickableDiv
			className={overrideClassList(
				{
					[`flex flex-col items-center justify-start transition-transform border border-[var(--palette-N100)] rounded-[4px] shadow-md bg-white px-4 py-2.5 [&.disabled]:opacity-40 `]: true,
					[`[&:not(.disabled)]:hover:-translate-y-0.5 [&:not(.disabled)]:focus-visible:-translate-y-0.5 [&:not(.disabled)]:active:translate-y-0 [&.active:not(.disabled)]:focus-visible:translate-y-0`]: true,
					"min-h-[120px]": size === "large",
					"min-h-[80px]": size === "small",
					disabled: props.disabled,
				},
				props.className,
			)}
			style={props.style}
			onClick={props.onClick}
			disabled={props.disabled}
			innerRef={props.innerRef}
			data-qualifier={props["data-qualifier"]}
		>
			<div className="[&.disabled]:bg-black">
				<Icon icon={props.icon} size={24} color={paletteToIconColor[palette]} />
			</div>
			<WrapIfStrOrNumOrEmpty
				wrapper={(content) => (
					<div className="h-8 flex items-center mt-1">
						<Text
							type="Body/M/Bold"
							color={themeCSSVars.palette_N500}
							title={String(content)}
							className="whitespace-pre-line text-center line-clamp-2"
						>
							{content}
						</Text>
					</div>
				)}
			>
				{props.children}
			</WrapIfStrOrNumOrEmpty>
			{props.subtitle && (
				<div className="mt-auto self-center pt-2">
					<hr style={{ color: themeCSSVars.palette_N200 }} className="mb-0.5 w-8" />
					<Text as="div" className="text-center" type="Body/S/Book">
						{props.subtitle}
					</Text>
				</div>
			)}
		</ClickableDiv>
	);
}

function Adders(props: {
	activeTab: ReportTemplateVariant;
	onAdd(item: ReportTemplateItem): void;
	ctx?: { reportTemplate: Omit<ReportTemplate, "uuid">; templateVariantTab: ReportTemplateVariant };
	onExpandChange(expand: boolean): void;
	expand: boolean;
	disabled?: boolean;
}) {
	return (
		<SideDrawer
			disableTransition
			expand={props.expand}
			onExpandChange={props.onExpandChange}
			widths={[24, 480]}
			title="Add element"
			footer={
				availableTemplateItemKindsByReportType[props.activeTab].length !== enabledTemplateItemKinds.length &&
				"* Not available in the selected tab"
			}
		>
			<div className="grid grid-cols-3 gap-2">
				<ForEach collection={enabledTemplateItemKinds} keyProvider={(kind) => kind}>
					{(reportItemKind) => {
						const canUseInTab = availableTemplateItemKindsByReportType[props.activeTab].includes(reportItemKind);
						const disabled = !props.ctx || !canUseInTab || !itemToConstraints[reportItemKind](props.ctx);
						return (
							<AutoTooltip
								align="middle"
								position="top"
								mode="hover"
								disabled={!disabled || props.disabled}
								overrideColor={themeCSSVars.palette_N200}
								trigger={({ innerRef }) => (
									<AddButton
										icon={itemToIconMap[reportItemKind]}
										onClick={() => (disabled ? undefined : props.onAdd(itemFactories[reportItemKind]()))}
										size="small"
										disabled={disabled || props.disabled}
										innerRef={innerRef}
										data-qualifier={qualifier.reportCustomisation.builder.widgetAdderOption(reportItemKind)}
									>
										{`${itemToAdderLabelMap[reportItemKind]}${!canUseInTab ? "*" : ""}`}
									</AddButton>
								)}
							>
								You have already added the widget to the template structure
							</AutoTooltip>
						);
					}}
				</ForEach>
			</div>
		</SideDrawer>
	);
}

function TemplateItemList(props: {
	list: Array<ReportTemplateItem>;
	onChange?(list: Array<ReportTemplateItem>): void;
	templateVariant: ReportTemplateVariant;
	mode?: "read" | "edit";
}): React.JSX.Element {
	const { mode = "edit" } = props;

	const signal = useUnmountAbortSignal();

	const onEdit =
		props.onChange &&
		((item: ReportTemplateItem) => {
			if (!props.onChange) {
				return;
			}
			const onChange = props.onChange;
			spawn<void>(
				adaptAnimatedNodeProvider(({ onHidden, reject, resolve, show }) => (
					<Sidebar
						open={show}
						onClose={() => reject()}
						width={440}
						mode="modal"
						onAnimationStateChange={(s) => s === "exited" && onHidden()}
					>
						{/* <div className={`pt-4 bg-[color:var(--palette-N100)] border-t border-t-[color:${themeCSSVars}] px-2`}> */}
						{(function () {
							switch (item.kind) {
								case "cover":
									return (
										<Cover
											configuration={item}
											onChange={(newConfiguration) => {
												onChange(replaceById(props.list, newConfiguration, ({ id }) => id));
												resolve();
											}}
											onCancel={reject}
											templateVariant={props.templateVariant}
										/>
									);
								case "disclaimer":
									return (
										<Disclaimer
											configuration={item}
											onChange={(newConfiguration) => {
												onChange(replaceById(props.list, newConfiguration, ({ id }) => id));
												resolve();
											}}
											onCancel={reject}
											templateVariant={props.templateVariant}
										/>
									);
								case "summary":
									return (
										<Summary
											configuration={item}
											onChange={(newConfiguration) => {
												onChange(replaceById(props.list, newConfiguration, ({ id }) => id));
												resolve();
											}}
											onCancel={reject}
											templateVariant={props.templateVariant}
										/>
									);
								case "commentary":
									return (
										<Commentary
											configuration={item}
											onChange={(newConfiguration) => {
												onChange(replaceById(props.list, newConfiguration, ({ id }) => id));
												resolve();
											}}
											onCancel={reject}
											templateVariant={props.templateVariant}
										/>
									);
								case "portfolioMetrics":
									return (
										<PortfolioMetrics
											configuration={item}
											onChange={(newConfiguration) => {
												onChange(replaceById(props.list, newConfiguration, ({ id }) => id));
												resolve();
											}}
											onCancel={reject}
											templateVariant={props.templateVariant}
										/>
									);
								case "exAntePortfolioMetrics":
									return (
										<ExAntePortfolioMetrics
											configuration={item}
											onChange={(newConfiguration) => {
												onChange(replaceById(props.list, newConfiguration, ({ id }) => id));
												resolve();
											}}
											onCancel={reject}
											templateVariant={props.templateVariant}
										/>
									);
								case "freeText":
									return (
										<FreeText
											configuration={item}
											onChange={(newConfiguration) => {
												onChange(replaceById(props.list, newConfiguration, ({ id }) => id));
												resolve();
											}}
											onCancel={reject}
											templateVariant={props.templateVariant}
										/>
									);
								case "composition": {
									if (item.currentColumnPreferences.length === 0) {
										const defaultCompositionConfig = extractWidgetConfig(
											defaultPortfolioTemplates.at(0)!,
											"composition",
										);
										item.currentColumnPreferences = defaultCompositionConfig?.currentColumnPreferences ?? [];
									}

									if (item.proposalColumnPreferences.length === 0) {
										const defaultCompositionConfig = extractWidgetConfig(
											defaultPortfolioTemplates.at(0)!,
											"composition",
										);
										item.proposalColumnPreferences = defaultCompositionConfig?.proposalColumnPreferences ?? [];
									}
									return (
										<Composition
											configuration={item}
											onChange={(newConfiguration) => {
												onChange(replaceById(props.list, newConfiguration, ({ id }) => id));
												resolve();
											}}
											onCancel={reject}
											templateVariant={props.templateVariant}
										/>
									);
								}
								case "performance":
									return (
										<Performance
											configuration={item}
											onChange={(newConfiguration) => {
												onChange(replaceById(props.list, newConfiguration, ({ id }) => id));
												resolve();
											}}
											onCancel={reject}
											templateVariant={props.templateVariant}
										/>
									);
								case "performanceAttribution":
									return (
										<PerformanceAttribution
											configuration={item}
											onChange={(newConfiguration) => {
												onChange(replaceById(props.list, newConfiguration, ({ id }) => id));
												resolve();
											}}
											onCancel={reject}
											templateVariant={props.templateVariant}
										/>
									);
								case "performanceAndVolatilityContribution":
									return (
										<PerformanceAndVolatilityContribution
											configuration={item}
											onChange={(newConfiguration) => {
												onChange(replaceById(props.list, newConfiguration, ({ id }) => id));
												resolve();
											}}
											onCancel={reject}
											templateVariant={props.templateVariant}
										/>
									);
								case "exAnteContributionVolatility":
									return (
										<ExAnteContributionVolatility
											configuration={item}
											onChange={(newConfiguration) => {
												onChange(replaceById(props.list, newConfiguration, ({ id }) => id));
												resolve();
											}}
											onCancel={reject}
											templateVariant={props.templateVariant}
										/>
									);
								case "factorExposure":
									return (
										<FactorExposure
											configuration={item}
											onChange={(newConfiguration) => {
												onChange(replaceById(props.list, newConfiguration, ({ id }) => id));
												resolve();
											}}
											onCancel={reject}
											templateVariant={props.templateVariant}
										/>
									);
								case "exposure":
									return (
										<Exposure
											configuration={item}
											onChange={(newConfiguration) => {
												onChange(replaceById(props.list, newConfiguration, ({ id }) => id));
												resolve();
											}}
											onCancel={reject}
											templateVariant={props.templateVariant}
										/>
									);
								case "bondsDividendsTracker":
									return (
										<BondsDividendsTracker
											configuration={item}
											onChange={(newConfiguration) => {
												onChange(replaceById(props.list, newConfiguration, ({ id }) => id));
												resolve();
											}}
											onCancel={reject}
											templateVariant={props.templateVariant}
										/>
									);
								case "maturityCouponTracker":
									return (
										<MaturityCouponTracker
											configuration={item}
											onChange={(newConfiguration) => {
												onChange(replaceById(props.list, newConfiguration, ({ id }) => id));
												resolve();
											}}
											onCancel={reject}
											templateVariant={props.templateVariant}
										/>
									);
							}
						})()}
						{/* </div> */}
					</Sidebar>
				)),
				{ signal },
			).promise.catch(noop);
		});

	return (
		<DraggableList
			itemKey={(item) => item.id}
			items={props.list}
			disabled={!props.onChange}
			onReorder={mode === "edit" ? props.onChange : undefined}
		>
			{({ dragHandleProps, isDragging, draggableProps, item, innerRef }) => (
				<TemplateItem
					item={item}
					dragHandleProps={dragHandleProps}
					isDragging={isDragging}
					draggableProps={mode === "edit" ? draggableProps : undefined}
					innerRef={innerRef}
					onEdit={mode === "edit" ? onEdit && (() => onEdit(item)) : undefined}
					onDelete={
						mode === "edit"
							? props.onChange && (() => props.onChange!(props.list.filter((x) => x.id !== item.id)))
							: undefined
					}
				/>
			)}
		</DraggableList>
	);
}
